/**
 * Códigos QR.
 *
 * El generador viene de `@zxing/library`, que ya está en el proyecto para leer
 * con la cámara. Se carga a pedido y no al arrancar: pesa, y de los códigos QR
 * sólo depende el diseñador de etiquetas, que usan los administradores.
 */

const CORRECCIONES = ['L', 'M', 'Q', 'H'] as const;
export type Correccion = (typeof CORRECCIONES)[number];
export const NIVELES_CORRECCION = CORRECCIONES;

/** Matriz de módulos: `true` es un cuadradito negro. */
export type MatrizQr = boolean[][];

const cache = new Map<string, MatrizQr>();

/**
 * La matriz del QR, o `null` si el texto no se puede codificar. Se pide el
 * mínimo tamaño y ZXing devuelve la versión que haga falta; los cuadraditos
 * los dibuja después quien corresponda, en la unidad que use.
 */
export async function matrizQr(texto: string, correccion: Correccion = 'M'): Promise<MatrizQr | null> {
  if (!texto) return null;

  const clave = `${correccion}:${texto}`;
  const guardada = cache.get(clave);
  if (guardada) return guardada;

  try {
    const { BarcodeFormat, EncodeHintType, MultiFormatWriter } = await import('@zxing/library');
    const pistas = new Map();
    pistas.set(EncodeHintType.ERROR_CORRECTION, correccion);
    pistas.set(EncodeHintType.MARGIN, 0);

    // El tamaño que se pide es el mínimo: ZXing agranda hasta que entre.
    const bits = new MultiFormatWriter().encode(texto, BarcodeFormat.QR_CODE, 1, 1, pistas);

    const matriz: MatrizQr = [];
    for (let y = 0; y < bits.getHeight(); y++) {
      const fila: boolean[] = [];
      for (let x = 0; x < bits.getWidth(); x++) fila.push(bits.get(x, y));
      matriz.push(fila);
    }

    cache.set(clave, matriz);
    return matriz;
  } catch {
    // Texto demasiado largo para la corrección elegida, por ejemplo.
    return null;
  }
}
